import React, { FC, useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../providers/AuthProvider";
import { Row, Col, Card } from "react-bootstrap";
import { BvIcon } from "bevi-icon";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import MasterPage from "../../MasterPage/MasterPage";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Treemap,
} from "recharts";
import { useProductivityDashboard } from "../../../providers/Dashboards/ProductivityDashboardProvider";
import { useSidebarFilter } from "../../../providers/SidebarFilterProvider";
import { useHeartbeat } from "../../../hooks/useHeartbeat";
import { useArticles } from "../../../providers/Articles/ArticlesProvider";
import { ArticlesProvider } from "../../../providers/Articles/ArticlesProvider";
import ModalArticleView from "../../../components/Articles/ModalArticleViewInsert/ModalArticleView";
import SidebarProductivityDashboardFilter from "../../../components/Dashboard/SidebarProductivityDashboardFilter/SidebarProductivityDashboardFilter";
import AppPagination from "../../../components/AppPagination/AppPagination";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import UserActiveIcon from "../../../assets/icons/UserActive.svg";
import ChartInsightsIcon from "../../../assets/icons/chart-insights.svg";
import RankingStarIcon from "../../../assets/icons/ranking-star.svg";
import { parseBackendDate } from "../../../utils/dateTimezoneParser";
import { useLazyLoad } from "../../../hooks/useLazyLoad";
import "./ProductivityDashboard.scss";

const COLORS = ["#00002E", "#000094", "#0000C7", "#0000FA", "#2E2EFF", "#6161FF", "#9494FF", "#C7C7FF", "#E5E5FF", "#F0F0FF"];
const ITEMS_PER_PAGE = 12;
const LOCALE = "pt-BR";
const DAYS_OF_WEEK = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

const ArticleModalWrapper: FC<{ show: boolean; article: any; onClose: () => void }> = ({ show, article, onClose }) => {
  const { handleGet } = useArticles();
  const [fullArticle, setFullArticle] = useState<any>(null);

  useEffect(() => {
    if (show && article?.id) {
      const fetchArticle = async () => {
        try {
          const data = await handleGet(article.id);
          setFullArticle(data);
        } catch (error) {
          console.error("Erro ao buscar artigo:", error);
        }
      };
      fetchArticle();
    } else {
      setFullArticle(null);
    }
  }, [show, article?.id, handleGet]);

  return (
    <ModalArticleView
      show={show}
      onClose={onClose}
      article={fullArticle}
    />
  );
};

const getInitials = (name?: string): string => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  return parts.length === 1
    ? parts[0].charAt(0).toUpperCase()
    : `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`;
};

const capitalize = (text?: string): string => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatNumber = (value: number) => new Intl.NumberFormat(LOCALE).format(value);

const formatTime = (minutes: number): string => {
  if (minutes < 60) {
    return `${Math.round(minutes)}min`;
  }

  if (minutes < 1440) { // < 24 horas
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}min`;
  }

  // >= 24 horas
  const days = Math.floor(minutes / 1440);
  const remainingMinutes = minutes % 1440;
  const hours = Math.floor(remainingMinutes / 60);
  const mins = Math.round(remainingMinutes % 60);
  return `${days}d ${hours}h ${mins}min`;
};

const TreemapRect = (props: any) => {
  const { x, y, width, height, index, payload, fill, chartData, onSelectArticle } = props;
  const originalColor = payload?.fill || fill || COLORS[index % COLORS.length];
  const padding = 4;
  const percentage = chartData?.[index]?.percentage || "0";

  const clearColors = ["#9494FF", "#C7C7FF"];
  const isLightColor = clearColors.includes(originalColor);
  const tarjaFill = isLightColor ? "#000000" : "#ffffff";
  const tarjaHeight = 28;
  const tarjaWidth = 50;
  const tarjaX = x + width - tarjaWidth - 6;
  const tarjaY = y + height - tarjaHeight - 6;

  return (
    <g
      onMouseEnter={() => {
        document.querySelectorAll('.treemap-rect').forEach((el) => {
          const elIndex = parseInt((el as HTMLElement).getAttribute('data-index') || '0');
          if (elIndex !== index) {
            (el as HTMLElement).style.filter = 'grayscale(1) opacity(0.15)';
          }
        });
        document.querySelectorAll('.quality-chart__row').forEach((el) => {
          const rowIndex = parseInt((el as HTMLElement).getAttribute('data-table-index') || '0');
          if (rowIndex === index) {
            (el as HTMLElement).style.backgroundColor = 'rgba(97, 97, 255, 0.1)';
          }
        });
      }}
      onMouseLeave={() => {
        document.querySelectorAll('.treemap-rect').forEach((el) => {
          (el as HTMLElement).style.filter = '';
        });
        document.querySelectorAll('.quality-chart__row').forEach((el) => {
          (el as HTMLElement).style.backgroundColor = '';
        });
      }}
      onClick={() => onSelectArticle(chartData[index])}
      className="cursor-pointer"
    >
      <rect
        x={x + padding}
        y={y + padding}
        width={width - padding * 2}
        height={height - padding * 2}
        fill={originalColor}
        fillOpacity="1"
        stroke="white"
        strokeWidth={0}
        rx={8}
        className="treemap-rect"
        data-index={index}
      />
      <rect
        x={tarjaX}
        y={tarjaY}
        width={tarjaWidth}
        height={tarjaHeight}
        fill={tarjaFill}
        fillOpacity={0.16}
        stroke="none"
        rx={4}
      />
      <text
        x={tarjaX + tarjaWidth / 2}
        y={tarjaY + tarjaHeight / 2 + 4}
        textAnchor="middle"
        fontSize="12"
        fill={tarjaFill}
        fillOpacity="0.48"
      >
        {percentage}%
      </text>
    </g>
  );
};

const RankingTooltip = (props: any) => {
  const { active, payload } = props;
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;

  return (
    <div className="dashboard-tooltip">
      <p className="dashboard-tooltip__title" title={data.fullTitle}>
        {data.fullTitle}
      </p>
    </div>
  );
};

const QualityTreemap: FC<{
  data: any[];
  onSelectArticle: (article: any) => void;
}> = React.memo(({ data, onSelectArticle }) => {
  const renderRect = useCallback((props: any) => (
    <TreemapRect {...props} chartData={data} onSelectArticle={onSelectArticle} />
  ), [data, onSelectArticle]);

  return (
    <ResponsiveContainer width="100%" height={400} minWidth={0} className="chart-background">
      <Treemap
        data={data}
        dataKey="value"
        stroke="#ffffff"
        fill="#ffffff"
        content={renderRect}
        isAnimationActive={false}
      >
        <Tooltip content={<RankingTooltip />} />
      </Treemap>
    </ResponsiveContainer>
  );
});

interface KPICardProps {
  icon?: string;
  iconImage?: string;
  label: string;
  value: string | number;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  isLoading?: boolean;
  helpText?: string;
  showInfoIcon?: boolean;
}

const KPICard: FC<KPICardProps> = ({
  icon,
  iconImage,
  label,
  value,
  trend,
  isLoading = false,
  helpText,
  showInfoIcon = false,
}) => {
  if (isLoading) {
    return (
      <Card className="kpi-card">
        <Card.Body>
          <Skeleton height={40} width="80%" />
          <Skeleton height={30} width="60%" className="mt-2" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="kpi-card">
      <div className="kpi-card__header">
        <div className="kpi-card__header-left">
          {iconImage && (
            <div className="kpi-card__icon">
              <img src={iconImage} alt={label} />
            </div>
          )}
          {icon && !iconImage && (
            <div className="kpi-card__icon">
              <BvIcon name={icon} color="#444688" width={28} height={28} />
            </div>
          )}
        </div>

        <div className="kpi-card__header-middle">
          <span className="kpi-card__label">{label}</span>
        </div>

        <div className="kpi-card__header-right">
          {trend && (
            <div className="kpi-card__trend">
              <div className={`kpi-card__badge ${trend.isPositive ? "positive" : "negative"}`}>
                <BvIcon
                  name={trend.isPositive ? "arrow-up" : "arrow-down"}
                  variant="solid"
                  color="#444688"
                  className={`kpi-card__badge-icon ${trend.isPositive ? "" : "rotated"}`}
                />
                <span>{Math.abs(trend.value)}%</span>
              </div>
            </div>
          )}

          {(helpText || showInfoIcon) && (
            <div className="kpi-card__help">
              <div className="kpi-card__help-icon" data-tooltip={helpText}>
                <BvIcon name={showInfoIcon ? "info" : "question-circle"} variant={showInfoIcon ? "light" : "solid"} width={showInfoIcon ? 24 : undefined} height={showInfoIcon ? 24 : undefined} color="#444688" />
              </div>
            </div>
          )}
        </div>
      </div>

      <Card.Body className="kpi-card__body">
        <div className="kpi-card__value">{value}</div>
      </Card.Body>
    </Card>
  );
};

interface DashboardChartProps {
  title: string;
  subtitle?: string;
  icon?: string;
  iconImage?: string;
  isLoading?: boolean;
  children: React.ReactNode;
  lg?: number;
  md?: number;
  sm?: number;
}

const DashboardChart: FC<DashboardChartProps> = ({
  title,
  subtitle,
  icon,
  iconImage,
  isLoading = false,
  children,
  lg = 6,
  md = 12,
  sm = 12,
}) => {
  return (
    <Col lg={lg} md={md} sm={sm}>
      <Card className="dashboard-chart">
        <Card.Header className="dashboard-chart__header">
          <div className="dashboard-chart__header-left">
            {iconImage && <img src={iconImage} alt={title} className="dashboard-chart__icon-image" />}
            {icon && !iconImage && <BvIcon name={icon} variant="solid" color="#444688" width={28} height={28} />}
          </div>
          <div className="dashboard-chart__header-middle">
            <h5 className="dashboard-chart__title">{title}</h5>
            {subtitle && <p className="dashboard-chart__subtitle">{subtitle}</p>}
          </div>
        </Card.Header>

        <Card.Body className="dashboard-chart__body">
          {isLoading ? (
            <div className="chart-container__loading">
              <Skeleton height={400} width="100%" />
            </div>
          ) : (
            children
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

interface LazyDashboardChartProps extends DashboardChartProps {
  shouldLoad?: boolean;
}

const LazyDashboardChart: FC<LazyDashboardChartProps> = ({
  shouldLoad = true,
  lg = 6,
  md = 12,
  sm = 12,
  ...props
}) => {
  const { ref, isVisible } = useLazyLoad();
  const isReady = shouldLoad && isVisible;

  return (
    <Col lg={lg} md={md} sm={sm} ref={ref}>
      <Card className="dashboard-chart">
        <Card.Header className="dashboard-chart__header">
          <div className="dashboard-chart__header-left">
            {props.iconImage && <img src={props.iconImage} alt={props.title} className="dashboard-chart__icon-image" />}
            {props.icon && !props.iconImage && <BvIcon name={props.icon} variant="solid" color="#444688" width={28} height={28} />}
          </div>
          <div className="dashboard-chart__header-middle">
            <h5 className="dashboard-chart__title">{props.title}</h5>
            {props.subtitle && <p className="dashboard-chart__subtitle">{props.subtitle}</p>}
          </div>
        </Card.Header>

        <Card.Body className="dashboard-chart__body">
          {isReady ? (
            <>
              {props.isLoading ? (
                <div className="chart-container__loading">
                  <Skeleton height={400} width="100%" />
                </div>
              ) : (
                props.children
              )}
            </>
          ) : (
            <div className="chart-container__loading">
              <Skeleton height={400} width="100%" />
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

interface Column {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
  align?: "left" | "center" | "right";
  width?: string;
}

interface DashboardTableProps {
  title: string;
  subtitle?: string;
  icon?: string;
  columns: Column[];
  data: any[];
  isLoading?: boolean;
  lg?: number;
  md?: number;
  sm?: number;
}

const DashboardTable: FC<DashboardTableProps> = ({
  title,
  subtitle,
  icon,
  columns,
  data,
  isLoading = false,
  lg = 6,
  md = 12,
  sm = 12,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = ITEMS_PER_PAGE;

  const totalItems = data?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = data?.slice(startIndex, startIndex + itemsPerPage) || [];

  const pages: PagesPaginateModel = {
    current: currentPage,
    first: 1,
    last: totalPages,
    previous: currentPage > 1 ? currentPage - 1 : undefined,
    next: currentPage < totalPages ? currentPage + 1 : undefined,
    total: totalItems,
    perPage: itemsPerPage,
    position: {
      first: totalItems > 0 ? startIndex + 1 : 0,
      last: Math.min(startIndex + itemsPerPage, totalItems),
    },
  };

  const handlePaginate = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Col lg={lg} md={md} sm={sm}>
      <Card className="dashboard-table">
        <Card.Header className="dashboard-table__header">
          <div className="dashboard-table__header-left">
            {icon && <BvIcon name={icon} variant="solid" color="#444688" width={28} height={28} />}
          </div>
          <div className="dashboard-table__header-middle">
            <h5 className="dashboard-table__title">{title}</h5>
            {subtitle && <p className="dashboard-table__subtitle">{subtitle}</p>}
          </div>
        </Card.Header>

        <Card.Body className="dashboard-table__body">
          {isLoading ? (
            <div className="chart-container__skeletons">
              <Skeleton count={5} height={40} className="mb-2" />
            </div>
          ) : (
            <>
              <div className="dashboard-table__wrapper">
                <table className="dashboard-table__table">
                  <thead className="dashboard-table__thead">
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          style={{ width: col.width }}
                          className={`text-${col.align || "left"}`}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="dashboard-table__tbody">
                    {paginatedData && paginatedData.length > 0 ? (
                      paginatedData.map((row, idx) => (
                        <tr key={idx} className="dashboard-table__row">
                          {columns.map((col) => (
                            <td
                              key={`${idx}-${col.key}`}
                              className={`text-${col.align || "left"}`}
                            >
                              {col.render ? col.render(row[col.key], row) : row[col.key]}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={columns.length} className="dashboard-table__empty text-center">
                          Nenhum dado disponível
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {(data?.length || 0) > itemsPerPage && (
                <nav aria-label="Paginação de sessões" className="dashboard-table__pagination">
                  <AppPagination
                    pages={pages}
                    handlePaginate={handlePaginate}
                    position="bottom"
                  />
                </nav>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

const ProductivityDashboard: FC = () => {
  const navigate = useNavigate();
  const { userLogged } = useAuth();
  const [rankingCurrentPage, setRankingCurrentPage] = useState(1);
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const rankingItemsPerPage = 10;

  useHeartbeat();

  useEffect(() => {
    if (userLogged && userLogged.access_profile_id !== 8 && userLogged.id !== 3 && userLogged.partner_code !== 'SP18-11074') {
      navigate('/home');
    }
  }, [userLogged, navigate]);

  const {
    filters,
    kpis,
    sessions,
    materials,
    ranking,
    peakHours,
    qualityRanking,
    isLoading,
    isLoadingKpis,
    isLoadingSessions,
    isLoadingMaterials,
    isLoadingRanking,
    isLoadingPeakHours,
    isLoadingQualityRanking,
  } = useProductivityDashboard();

  const sessionsColumns = [
    {
      key: "collaborator_name",
      label: "Colaborador",
      width: "40%",
      render: (value: string) => capitalize(value),
    },
    {
      key: "total_session_minutes",
      label: "Tempo Ativo",
      width: "25%",
      align: "center" as const,
      render: (value: number, row: any) => formatTime(value - (row.idle_time_minutes || 0)),
    },
    {
      key: "session_count",
      label: "Sessões",
      width: "15%",
      align: "center" as const,
    },
    {
      key: "last_login",
      label: "Último Acesso",
      width: "20%",
      align: "right" as const,
      render: (value: string) =>
        value ? new Date(value).toLocaleDateString("pt-BR") : "—",
    },
  ];


  const aggregateChartDataByPeriod = (data: any[], period: string | null | undefined) => {
    if (!data || data.length === 0) return [];

    if (period === "year") {
      const grouped: { [key: string]: any } = {};
      data.forEach((item: any) => {
        const date = parseBackendDate(item.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        const monthName = date.toLocaleString("pt-BR", { month: "short" });
        const monthNum = date.getMonth() + 1;

        if (!grouped[monthKey]) {
          grouped[monthKey] = {
            date: monthNum,
            displayDate: monthName,
            articles_created: 0,
            articles_edited: 0,
          };
        }
        grouped[monthKey].articles_created += item.articles_created || 0;
        grouped[monthKey].articles_edited += item.articles_edited || 0;
      });
      return Object.values(grouped).sort((a: any, b: any) => a.date - b.date);
    }

    if (period === "month") {
      const grouped: { [key: string]: any } = {};
      data.forEach((item: any) => {
        const date = parseBackendDate(item.date);
        const dayOfMonth = date.getDate();
        const weekNum = Math.ceil(dayOfMonth / 7);
        const weekKey = `sem-${weekNum}`;

        if (!grouped[weekKey]) {
          const startDay = (weekNum - 1) * 7 + 1;
          const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          const endDay = Math.min(weekNum * 7, lastDayOfMonth);

          grouped[weekKey] = {
            date: weekNum,
            displayDate: `${startDay}-${endDay}`,
            articles_created: 0,
            articles_edited: 0,
          };
        }
        grouped[weekKey].articles_created += item.articles_created || 0;
        grouped[weekKey].articles_edited += item.articles_edited || 0;
      });
      return Object.values(grouped).sort((a: any, b: any) => a.date - b.date);
    }

    if (period === "week") {
      const grouped: { [key: number]: any } = {};

      for (let i = 0; i <= 6; i++) {
        grouped[i] = {
          date: i,
          displayDate: DAYS_OF_WEEK[i],
          articles_created: 0,
          articles_edited: 0,
        };
      }

      data.forEach((item: any) => {
        const date = parseBackendDate(item.date);
        const dayOfWeek = date.getDay();

        if (grouped[dayOfWeek]) {
          grouped[dayOfWeek].articles_created += item.articles_created || 0;
          grouped[dayOfWeek].articles_edited += item.articles_edited || 0;
        }
      });

      const result = [];
      for (let i = 0; i <= 6; i++) {
        result.push(grouped[i]);
      }
      return result;
    }

    if (period === "day") {
      const grouped: { [key: string]: any } = {};
      data.forEach((item: any) => {
        const date = parseBackendDate(item.date);
        const hour = String(date.getHours()).padStart(2, "0");
        const hourKey = hour;

        if (!grouped[hourKey]) {
          grouped[hourKey] = {
            date: parseInt(hour),
            displayDate: `${hour}:00`,
            articles_created: 0,
            articles_edited: 0,
          };
        }
        grouped[hourKey].articles_created += item.articles_created || 0;
        grouped[hourKey].articles_edited += item.articles_edited || 0;
      });
      return Object.values(grouped).sort((a: any, b: any) => a.date - b.date);
    }

    if (period === "last_60_days" || period === "last_90_days") {
      const grouped: { [key: string]: any } = {};
      data.forEach((item: any) => {
        const date = parseBackendDate(item.date);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay()); // Início da semana (domingo)
        const weekKey = weekStart.toISOString().split("T")[0];

        if (!grouped[weekKey]) {
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          const startDay = weekStart.getDate();
          const endDay = weekEnd.getDate();
          const startMonth = weekStart.toLocaleString("pt-BR", { month: "short" });
          const endMonth = weekEnd.toLocaleString("pt-BR", { month: "short" });

          grouped[weekKey] = {
            date: weekStart.getTime(),
            displayDate: startMonth === endMonth
              ? `${startDay}-${endDay} ${startMonth}`
              : `${startDay} ${startMonth} - ${endDay} ${endMonth}`,
            articles_created: 0,
            articles_edited: 0,
          };
        }
        grouped[weekKey].articles_created += item.articles_created || 0;
        grouped[weekKey].articles_edited += item.articles_edited || 0;
      });
      return Object.values(grouped).sort((a: any, b: any) => a.date - b.date);
    }

    if (!period) {
      return data
        .map((item: any) => {
          const date = parseBackendDate(item.date);
          const day = date.getDate();
          const monthName = date.toLocaleString("pt-BR", { month: "short" });
          return {
            date: date.getTime(),
            displayDate: `${day} ${monthName}`,
            articles_created: item.articles_created || 0,
            articles_edited: item.articles_edited || 0,
          };
        })
        .sort((a: any, b: any) => a.date - b.date);
    }

    return [...data].sort((a: any, b: any) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  };

  const materialsChartData = useMemo(() =>
    aggregateChartDataByPeriod(materials?.time_series || [], filters.period),
    [materials, filters.period]
  );

  const chartTotals = useMemo(() => {
    return {
      articles_created: materialsChartData.reduce((sum: number, item: any) => sum + (item.articles_created || 0), 0),
      articles_edited: materialsChartData.reduce((sum: number, item: any) => sum + (item.articles_edited || 0), 0),
    };
  }, [materialsChartData]);

  const totalActiveTimeHours = useMemo(() => {
    const collaborators = sessions?.collaborators || [];
    const totalMinutes = collaborators.reduce((sum: number, item: any) => {
      const activeMinutes = (item.total_session_minutes || 0) - (item.idle_time_minutes || 0);
      return sum + activeMinutes;
    }, 0);
    return totalMinutes / 60;
  }, [sessions]);

  const MaterialsTooltip = (props: any) => {
    const { active, payload } = props;
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;

    return (
      <div className="materials-tooltip">
        <p className="materials-tooltip__date">{data.displayDate || data.date}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="materials-tooltip__item">
            <BvIcon
              name={entry.dataKey === "articles_created" ? "article" : "editSquare"}
              variant="dark"
              size={1.2}
              color="#444688"
            />
            <span>{entry.name}: </span>
            <span style={{ color: entry.color, fontWeight: 600 }}>{formatNumber(entry.value)}</span>
          </p>
        ))}
      </div>
    );
  };

  const PeakHoursTooltip = (props: any) => {
    const { active, payload, label } = props;
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div className="materials-tooltip">
        <p className="materials-tooltip__item">
          <BvIcon name="watchLater" size={1.2} color="#444688" />
          <span>Hora {label}h</span>
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="materials-tooltip__item">
            <BvIcon name="factCheck" size={1.2} color="#444688" />
            <span>{entry.name}: </span>
            <span style={{ color: entry.color, fontWeight: 600 }}>{formatNumber(entry.value)}</span>
          </p>
        ))}
      </div>
    );
  };

  const peakHoursChartData = useMemo(() => {
    return peakHours?.peak_hours || [];
  }, [peakHours]);

  const peakHoursStats = useMemo(() => {
    const data = peakHoursChartData;
    if (!data || data.length === 0) return { maxHour: null, minHour: null };

    const maxHour = data.reduce((prev: any, current: any) =>
      (prev.activity_count > current.activity_count) ? prev : current
    );

    const minHour = data.reduce((prev: any, current: any) =>
      (prev.activity_count < current.activity_count) ? prev : current
    );

    return { maxHour, minHour };
  }, [peakHoursChartData]);

  const rankingChartData = useMemo(() => {
    const topRanking = ranking?.rankings?.slice(0, 10) || [];
    return topRanking
      .filter((item: any) => item.total_edits > 0)
      .sort((a: any, b: any) => (b.total_edits || 0) - (a.total_edits || 0))
      .map((item: any) => ({
        name: item.collaborator_name,
        value: item.total_edits,
        "Edições": item.total_edits,
      }));
  }, [ranking]);

  const rankingChartTotal = useMemo(() => {
    return rankingChartData.reduce((sum: number, item: any) => sum + item.value, 0);
  }, [rankingChartData]);

  const RankingBarTooltip = (props: any) => {
    const { active, payload } = props;
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;

    return (
      <div className="materials-tooltip">
        <p className="materials-tooltip__item">
          <BvIcon name="person" variant="dark" size={1.2} color="#444688" />
          <span>{data.name}</span>
        </p>
        <p className="materials-tooltip__item">
          <BvIcon name="editSquare" variant="dark" size={1.2} color="#444688" />
          <span>Edições: </span>
          <span style={{ color: "#6161ff", fontWeight: 600 }}>{formatNumber(data.value)}</span>
        </p>
      </div>
    );
  };

  const CustomBarLabel = (props: any) => {
    const { x, y, width, height, value, fill } = props;
    const percentage = ((value / rankingChartTotal) * 100).toFixed(0);
    const barCenterX = x + width / 2;
    const labelWidth = width * 0.75;
    const labelHeight = 24;
    const labelY = y + height - labelHeight - 4;
    const labelX = barCenterX - labelWidth / 2;

    const isLightColor = COLORS.indexOf(fill) >= 7;
    const tarjaColor = isLightColor ? "rgba(0, 0, 0, 0.16)" : "rgba(255, 255, 255, 0.16)";

    const tl = 2;
    const tr = 2;
    const br = 5;
    const bl = 5;

    const pathData = `
      M ${labelX + tl} ${labelY}
      L ${labelX + labelWidth - tr} ${labelY}
      Q ${labelX + labelWidth} ${labelY} ${labelX + labelWidth} ${labelY + tr}
      L ${labelX + labelWidth} ${labelY + labelHeight - br}
      Q ${labelX + labelWidth} ${labelY + labelHeight} ${labelX + labelWidth - br} ${labelY + labelHeight}
      L ${labelX + bl} ${labelY + labelHeight}
      Q ${labelX} ${labelY + labelHeight} ${labelX} ${labelY + labelHeight - bl}
      L ${labelX} ${labelY + tl}
      Q ${labelX} ${labelY} ${labelX + tl} ${labelY}
      Z
    `;

    return (
      <g>
        <path
          d={pathData}
          fill={tarjaColor}
        />
        <text
          x={barCenterX}
          y={labelY + labelHeight / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontSize="11"
          fontWeight="600"
        >
          {percentage}%
        </text>
      </g>
    );
  };


  const sessionsTableData = useMemo(() => {
    const collaborators = sessions?.collaborators || [];
    return [...collaborators].sort((a: any, b: any) => {
      const activeA = (a.total_session_minutes || 0) - (a.idle_time_minutes || 0);
      const activeB = (b.total_session_minutes || 0) - (b.idle_time_minutes || 0);
      return activeB - activeA;
    });
  }, [sessions]);

  const rankingTableData = useMemo(() => {
    const data = ranking?.rankings || [];
    const collaboratorsMap = new Map(
      (sessions?.collaborators || []).map((c: any) => [c.collaborator_id, c])
    );

    return [...data]
      .sort((a: any, b: any) => b.total_edits - a.total_edits)
      .map((item: any) => {
        const sessionData = collaboratorsMap.get(item.collaborator_id) as any;
        return {
          ...item,
          active_time_minutes: item.active_time_minutes || sessionData?.heartbeat_count || 0,
        };
      });
  }, [ranking, sessions]);

  const rankingTotalItems = rankingTableData?.length || 0;
  const rankingTotalPages = Math.ceil(rankingTotalItems / rankingItemsPerPage);
  const rankingStartIndex = (rankingCurrentPage - 1) * rankingItemsPerPage;
  const paginatedRankingData = rankingTableData?.slice(rankingStartIndex, rankingStartIndex + rankingItemsPerPage) || [];

  const rankingPages: PagesPaginateModel = {
    current: rankingCurrentPage,
    first: 1,
    last: rankingTotalPages,
    previous: rankingCurrentPage > 1 ? rankingCurrentPage - 1 : undefined,
    next: rankingCurrentPage < rankingTotalPages ? rankingCurrentPage + 1 : undefined,
    total: rankingTotalItems,
    perPage: rankingItemsPerPage,
    position: {
      first: rankingTotalItems > 0 ? rankingStartIndex + 1 : 0,
      last: Math.min(rankingStartIndex + rankingItemsPerPage, rankingTotalItems),
    },
  };

  const handleRankingPaginate = (page: number) => {
    setRankingCurrentPage(page);
  };

  const qualityRankingChartData = useMemo(() => {
    const rankings = qualityRanking?.rankings || [];
    const topCount = 5;
    const top = rankings.slice(0, topCount);
    const topColors = ["#00002E", "#000094", "#0000FA", "#6161FF", "#9494FF"];

    const total = top.reduce((sum: number, item: any) => sum + (item.correction_count || 0), 0) || 1;

    return top.map((item: any, index: number) => ({
      id: item.article_id,
      name: item.title?.substring(0, 50),
      value: item.correction_count,
      fullTitle: item.title,
      fill: topColors[index % topColors.length],
      percentage: ((item.correction_count || 0) / total * 100).toFixed(1),
      title: item.title,
    }));
  }, [qualityRanking]);

  const { showSidebarFilter, setShowSidebarFilter } = useSidebarFilter();

  const dashboards = [
    { value: 'produtividade', label: 'Produtividade' },
    { value: 'ai-usage', label: 'Uso da IA' },
  ];

  const renderContent = () => (
    <div className="productivity-dashboard">
      <div className="productivity-dashboard__header">
        <h5 className="productivity-dashboard__title">Produtividade</h5>
        <div className="productivity-dashboard__controls">
          <div className="productivity-dashboard__select-wrapper">
            <select
              className="productivity-dashboard__dashboard-select"
              defaultValue="produtividade"
              onChange={(e) => {
                if (e.target.value === 'ai-usage') {
                  window.location.href = '/dashboards/ai-usage';
                }
              }}
            >
              {dashboards.map((dashboard) => (
                <option key={dashboard.value} value={dashboard.value}>
                  {dashboard.label}
                </option>
              ))}
            </select>
            <BvIcon
              name="chevronDown"
              color="#25CBDB"
              width={16}
              height={16}
              className="productivity-dashboard__select-icon"
            />
          </div>
          <button
            type="button"
            className="btn btn-filter-dark btn-sm mb-0"
            onClick={() => setShowSidebarFilter(!showSidebarFilter)}
          >
            <BvIcon
              name="filter"
              variant="light"
              width={16}
              height={16}
              className="me-2"
              color="#25CBDB"
            />
            <span>Filtrar</span>
          </button>
        </div>
      </div>

      <Row className="g-3 row--compact">
          <Col lg={3} md={6} sm={12}>
            <KPICard
              icon="article"
              label="Artigos Criados"
              value={formatNumber(chartTotals.articles_created)}
              isLoading={isLoadingMaterials}
              showInfoIcon
              helpText="Total de artigos criados por todos os colaboradores"
            />
          </Col>
          <Col lg={3} md={6} sm={12}>
            <KPICard
              icon="editSquare"
              label="Artigos Editados"
              value={formatNumber(chartTotals.articles_edited)}
              isLoading={isLoadingMaterials}
              showInfoIcon
              helpText="Total de artigos editados por todos os colaboradores"
            />
          </Col>
          <Col lg={3} md={6} sm={12}>
            <KPICard
              iconImage={UserActiveIcon}
              label="Colaboradores Ativos"
              value={kpis?.kpis?.total_collaborators_active || 0}
              isLoading={isLoadingKpis}
              showInfoIcon
              helpText="Número de colaboradores que tiveram atividade no período"
            />
          </Col>
          <Col lg={3} md={6} sm={12}>
            <KPICard
              icon="watchLater"
              label="Tempo Ativo (horas)"
              value={formatNumber(totalActiveTimeHours) + "h"}
              isLoading={isLoadingSessions}
              showInfoIcon
              helpText="Tempo total que os colaboradores estiveram ativos"
            />
          </Col>
        </Row>

        <Row className="mb-3 g-3">
          <DashboardChart
            title="Produção de Materiais (Série Temporal)"
            subtitle="Artigos criados e editados ao longo do tempo"
            icon="construction"
            isLoading={isLoadingMaterials}
            lg={6}
          >
            <div className="materials-chart__container">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={materialsChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#cccccc" />
                  <XAxis dataKey="displayDate" stroke="#cccccc" />
                  <YAxis stroke="#cccccc" />
                  <Tooltip content={<MaterialsTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="articles_created"
                    stroke="#6161ff"
                    strokeWidth={2}
                    name="Criados"
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="articles_edited"
                    stroke="#25CBDB"
                    strokeWidth={2}
                    name="Editados"
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>

              <div className="legend">
                <div className="legend__item">
                  <div className="legend__box" style={{ backgroundColor: "#6161ff" }} />
                  <span className="legend-text legend-text--created">Criados</span>
                </div>
                <div className="legend__item">
                  <div className="legend__box" style={{ backgroundColor: "#25CBDB" }} />
                  <span className="legend-text legend-text--edited">Editados</span>
                </div>
              </div>

              <div
                className="materials-chart__table-wrapper"
                style={
                  materialsChartData && materialsChartData.length > 12
                    ? { maxHeight: '400px', overflowY: 'auto' }
                    : {}
                }
              >
                <table className="materials-chart__table">
                  <thead className="materials-chart__thead">
                    <tr>
                      <th>Data</th>
                      <th className="text-center">Criados</th>
                      <th className="text-center">Editados</th>
                    </tr>
                  </thead>
                  <tbody className="materials-chart__tbody">
                    {materialsChartData && materialsChartData.length > 0 ? (
                      materialsChartData.map((item: any, index: number) => (
                        <tr key={index}>
                          <td>{item.displayDate || item.date}</td>
                          <td className="text-center created">{item.articles_created}</td>
                          <td className="text-center edited">{item.articles_edited}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="materials-chart__empty-cell">
                          Nenhum dado disponível
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </DashboardChart>

          <LazyDashboardChart
            title="Horários de Pico de Atividade"
            subtitle="Distribuição de atividades por hora do dia"
            iconImage={ChartInsightsIcon}
            isLoading={isLoadingPeakHours}
            lg={6}
          >
            <div className="peak-hours-chart__container chart-container--tall">
              <ResponsiveContainer width="100%" height={630}>
                <AreaChart data={peakHoursChartData}>
                  <defs>
                    <linearGradient id="colorActivity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6161ff" stopOpacity="0.32" />
                      <stop offset="100%" stopColor="#6161ff" stopOpacity="0.04" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#cccccc" />
                  <XAxis dataKey="hour" stroke="#cccccc" />
                  <YAxis stroke="#cccccc" />
                  <Tooltip content={<PeakHoursTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="activity_count"
                    stroke="#6161ff"
                    strokeWidth={3}
                    strokeOpacity={1}
                    fill="url(#colorActivity)"
                    fillOpacity={1}
                    dot={(props: any) => {
                      const { cx, cy, payload } = props;
                      let fill = "#6161ff";
                      if (peakHoursStats.maxHour?.hour === payload.hour) {
                        fill = "#454787";
                      } else if (peakHoursStats.minHour?.hour === payload.hour) {
                        fill = "#A8A8FF";
                      }
                      return (
                        <circle cx={cx} cy={cy} r={5} fill={fill} />
                      );
                    }}
                    activeDot={{ r: 7 }}
                    name="Atividades"
                  />
                </AreaChart>
              </ResponsiveContainer>

              <div className="peak-hours-chart__table-wrapper">
                <table className="peak-hours-chart__table">
                  <thead className="peak-hours-chart__thead">
                    <tr>
                      <th>Descrição</th>
                      <th className="text-center">Horário</th>
                      <th className="text-center">Atividades</th>
                    </tr>
                  </thead>
                  <tbody className="peak-hours-chart__tbody">
                    <tr>
                      <td>
                        <span className="peak-stat">
                          <div className="peak-stat__box" style={{ backgroundColor: "#454787" }} />
                          <span className="peak-stat__text peak-stat__text--max">Maior Pico</span>
                        </span>
                      </td>
                      <td className="text-center">
                        {peakHoursStats.maxHour ? `${peakHoursStats.maxHour.hour}h` : "—"}
                      </td>
                      <td className="text-center">
                        {peakHoursStats.maxHour ? formatNumber(peakHoursStats.maxHour.activity_count) : "—"}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <span className="peak-stat">
                          <div className="peak-stat__box" style={{ backgroundColor: "#A8A8FF" }} />
                          <span className="peak-stat__text peak-stat__text--min">Menor Pico</span>
                        </span>
                      </td>
                      <td className="text-center">
                        {peakHoursStats.minHour ? `${peakHoursStats.minHour.hour}h` : "—"}
                      </td>
                      <td className="text-center">
                        {peakHoursStats.minHour ? formatNumber(peakHoursStats.minHour.activity_count) : "—"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </LazyDashboardChart>
        </Row>

        <Row className="g-3 row--extra-compact">
          <Col lg={4} md={12} sm={12}>
            <DashboardChart
              title="Top 10 Colaboradores (Edições)"
              subtitle="Ranking dos maiores editores"
              icon="penSignature"
              isLoading={isLoadingRanking}
              lg={12}
            >
              <div className="ranking-card__container chart-container--medium">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={rankingChartData}
                    margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                    barCategoryGap={rankingChartData.length < 5 ? "40%" : "15%"}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#cccccc" vertical={false} />
                    <XAxis dataKey="name" stroke="#cccccc" tick={false} />
                    <YAxis stroke="#cccccc" />
                    <Tooltip content={<RankingBarTooltip />} cursor={false} />
                    <Bar
                      dataKey="Edições"
                      fill="#6161ff"
                      radius={[8, 8, 8, 8]}
                      isAnimationActive={false}
                      label={<CustomBarLabel />}
                    >
                      {rankingChartData.map((_: any, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>

                <div className="ranking-card__legend-wrapper">
                  <table className="ranking-card__table">
                    <thead className="ranking-card__thead">
                      <tr>
                        <th>Colaborador</th>
                        <th className="text-right">Edições</th>
                      </tr>
                    </thead>
                    <tbody className="ranking-card__tbody">
                      {rankingChartData.map((item: any, index: number) => (
                        <tr key={index}>
                          <td className="text-left">
                            <span className="ranking-card__color-box" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                            {capitalize(item.name)}
                          </td>
                          <td className="text-right">{formatNumber(item.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </DashboardChart>
          </Col>

          <Col lg={8} md={12} sm={12}>
            <LazyDashboardChart
              title="Ranking Completo"
              subtitle="Desempenho completo de todos os criadores e editores de artigos"
              icon="leaderboard"
              isLoading={isLoadingRanking}
              lg={12}
            >
              <div className="ranking-card__wrapper">
                <table className="ranking-card__table">
                  <thead className="ranking-card__thead">
                    <tr>
                      <th>Colaborador</th>
                      <th className="text-center">Artigos</th>
                      <th className="text-center">Edições</th>
                      <th className="text-center ranking-card__medal-column">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody className="ranking-card__tbody">
                    {paginatedRankingData.map((item: any, index: number) => {
                      const actualIndex = rankingStartIndex + index;
                      return (
                        <tr key={actualIndex}>
                          <td className="text-left ranking-card__collaborator-cell">
                            {item.user_profile_image ? (
                              <img
                                src={`${process.env.REACT_APP_BEVIAJUDA_API}${item.user_profile_image}`}
                                alt={item.collaborator_name}
                                className="ranking-card__avatar"
                              />
                            ) : (
                              <div className="ranking-card__avatar ranking-card__avatar-placeholder d-flex align-items-center justify-content-center">
                                <span className="ranking-card__initials">{getInitials(item.collaborator_name)}</span>
                              </div>
                            )}
                            <span>{capitalize(item.collaborator_name)}</span>
                          </td>
                          <td className="text-center">{formatNumber(item.articles_created)}</td>
                          <td className="text-center">{formatNumber(item.total_edits)}</td>
                          <td className="text-center">
                            {actualIndex < 3 && <BvIcon name="medalHonor" variant="dark" size={1.6} color="#444688" />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {rankingTotalItems > rankingItemsPerPage && (
                  <nav aria-label="Paginação do ranking">
                    <AppPagination
                      pages={rankingPages}
                      handlePaginate={handleRankingPaginate}
                      position="bottom"
                    />
                  </nav>
                )}
              </div>
            </LazyDashboardChart>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg={6} md={12} sm={12}>
            <DashboardTable
              title="Sessões por Colaborador"
              subtitle="Número de sessões ativas por colaborador"
              icon="devices"
              columns={sessionsColumns}
              data={sessionsTableData}
              isLoading={isLoading}
              lg={12}
            />
          </Col>

          <Col lg={6} md={12} sm={12}>
            <LazyDashboardChart
              title="Top Conteúdos com Mais Ajustes"
              subtitle="Ranking de artigos por quantidade de correções"
              iconImage={RankingStarIcon}
              isLoading={isLoadingQualityRanking}
              lg={12}
            >
              {qualityRankingChartData && qualityRankingChartData.length > 0 ? (
                <div className="quality-chart__container chart-container--medium">
                  <div className="quality-chart__chart-wrapper">
                    <QualityTreemap
                      data={qualityRankingChartData}
                      onSelectArticle={setSelectedArticle}
                    />
                  </div>

                  <div className="quality-chart__legend-wrapper">
                    <table className="quality-chart__table">
                      <thead className="quality-chart__thead">
                        <tr>
                          <th>Conteúdo</th>
                          <th className="text-center">Correções</th>
                          <th className="text-center">%</th>
                        </tr>
                      </thead>
                      <tbody className="quality-chart__tbody">
                        {qualityRankingChartData.map((item: any, index: number) => (
                          <tr key={index} data-table-index={index} className="quality-chart__row">
                            <td className="text-left">
                              <span className="quality-chart__color-box" style={{ backgroundColor: item.fill }}></span>
                              {item.name}
                            </td>
                            <td className="text-center">{formatNumber(item.value)}</td>
                            <td className="text-center">{item.percentage}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="quality-chart__empty">
                  <p>Sem dados disponíveis</p>
                </div>
              )}
            </LazyDashboardChart>
          </Col>
        </Row>
    </div>
  );

  return (
    <MasterPage title="Produtividade" icon="fas fa-chart-line">
      <div className="ProductivityDashboard" data-testid="ProductivityDashboard">
        {renderContent()}
      </div>

      <SidebarProductivityDashboardFilter />

      <ArticlesProvider>
        <ArticleModalWrapper
          show={!!selectedArticle}
          article={selectedArticle}
          onClose={() => setSelectedArticle(null)}
        />
      </ArticlesProvider>
    </MasterPage>
  );
};

export default ProductivityDashboard;
